import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import DrawingTools from '@/components/DrawingTools';
import axios from 'axios';
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, createSeriesMarkers } from 'lightweight-charts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

import { BACKEND_URL, API, WS_URL } from '../config';

const TradingPro = () => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const wsRef = useRef(null);
  // Refs para descartar respuestas HTTP obsoletas cuando cambia símbolo/exchange
  const activeSymbolRef = useRef('');
  const activeExchangeRef = useRef('');

  const TOP_MARKETS = ['BTC/USDT','ETH/USDT','BNB/USDT','XRP/USDT','SOL/USDT','ADA/USDT','DOGE/USDT','DOT/USDT','AVAX/USDT','MATIC/USDT','LTC/USDT','LINK/USDT','UNI/USDT','ATOM/USDT','NEAR/USDT','OP/USDT','ARB/USDT','FTM/USDT','APT/USDT','SUI/USDT'];

  const [exchanges, setExchanges] = useState([]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [markets, setMarkets] = useState(TOP_MARKETS);
  const [filteredMarkets, setFilteredMarkets] = useState(TOP_MARKETS);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const _urlParams = new URLSearchParams(window.location.search);
  const _urlSymbol = _urlParams.get('symbol');
  const _urlExchange = _urlParams.get('exchange');
  const [selectedSymbol, setSelectedSymbol] = useState(_urlSymbol || 'BTC/USDT');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h');
  const [balance, setBalance] = useState(null);
  const [paperBal, setPaperBal] = useState(null);
  const [investmentActive, setInvestmentActive] = useState(false);
  const [manualEquity, setManualEquity] = useState([]);
  const [livePrice, setLivePrice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Precio en vivo: WS + fallback REST cada 3s
  const liveCandleRef = useRef(null);
  // Último "time" que el gráfico aceptó (setData o update) -- lightweight-charts
  // rechaza con una excepción cualquier update() con time <= al último que
  // ya tiene cargado, y como el time de la vela en vivo no cambia hasta que
  // termina el período, un solo choque dejaba la vela congelada el resto de
  // la hora (el error quedaba silenciado por el try/catch de arriba, por
  // eso el precio numérico seguía moviéndose pero la vela no).
  const lastChartTimeRef = useRef(0);

  const updateLivePrice = (price) => {
    if (!price) return;
    setLivePrice(prev => ({ ...prev, last: price, change: prev?.last ? ((price - prev.last) / prev.last) * 100 : 0 }));
    if (!chartRef.current) return;
    const tfSecs = {'1m':60,'5m':300,'15m':900,'30m':1800,'1h':3600,'2h':7200,'4h':14400,'1d':86400}[selectedTimeframe] || 3600;
    const candleTime = Math.floor(Date.now() / 1000 / tfSecs) * tfSecs;
    // Nunca intentar retroceder respecto al time ya cargado -- en vez de
    // arriesgar la excepción, se salta este tick (el precio numérico de
    // arriba ya se actualizó igual) y se reintenta con el próximo.
    if (candleTime < lastChartTimeRef.current) return;
    if (!liveCandleRef.current || liveCandleRef.current.time !== candleTime) {
      liveCandleRef.current = { time: candleTime, open: price, high: price, low: price, close: price };
    } else {
      liveCandleRef.current.close = price;
      if (price > liveCandleRef.current.high) liveCandleRef.current.high = price;
      if (price < liveCandleRef.current.low) liveCandleRef.current.low = price;
    }
    try {
      chartRef.current.series.update(liveCandleRef.current);
      lastChartTimeRef.current = candleTime;
    } catch (_e) {
      // Choque igual a pesar del guard (ej. carrera entre dos ticks casi
      // simultáneos) -- no queda pegado: se descarta la vela en memoria y el
      // próximo tick la reconstruye de cero en vez de repetir el mismo time.
      liveCandleRef.current = null;
    }
  };

  useEffect(() => {
    if (!selectedExchange || !selectedSymbol) return;
    let ws, wsOk = false, restId;

    // WS para precio en vivo — activo es false al limpiar para evitar zombie WS
    let activo = true;
    const connect = () => {
      if (!activo) return;
      try {
        ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onopen = () => { wsOk = true; };
        ws.onmessage = (e) => {
          if (!activo) return;
          try {
            const d = JSON.parse(e.data);
            if (d.symbol === selectedSymbol && d.exchange === selectedExchange && d.last) {
              updateLivePrice(d.last);
            }
          } catch {}
        };
        ws.onclose = () => { wsOk = false; if (activo) setTimeout(connect, 5000); };
        ws.onerror = () => { wsOk = false; };
      } catch {}
    };
    connect();

    // REST polling — siempre activo para garantizar precio inicial
    const poll = async () => {
      if (!activo) return;
      try {
        const r = await axios.get(`${API}/market/ticker/${selectedExchange}`,
          { params: { symbol: selectedSymbol }, withCredentials: true });
        if (activo && r.data?.last) updateLivePrice(r.data.last);
      } catch {}
    };
    poll();
    restId = setInterval(poll, 3000);

    return () => {
      activo = false;  // Mata el zombie — ningún reconect ni update después de esto
      if (ws) { ws.onclose = null; ws.close(); }
      clearInterval(restId);
    };
  }, [selectedExchange, selectedSymbol, selectedTimeframe]); // eslint-disable-line

  // Indicadores profesionales del gráfico (interruptores)
  const [indicators, setIndicators] = useState({ ema20: true, ema50: true, bb: false, vwap: false, volume: true, botArrows: false, manualArrows: false });
  const [indReadout, setIndReadout] = useState(null); // RSI / MACD en vivo
  const lastDataRef = useRef(null);

  const [orderForm, setOrderForm] = useState({
    side: 'buy',
    type: 'market',
    amount: '',
    price: '',
    market_type: 'spot',
    leverage: 1,
    stop_loss: '',
    take_profit: '',
    paper_trading: true
  });

  const [openTrades, setOpenTrades] = useState([]);
  const [minOrder, setMinOrder] = useState(null);

  const fetchMinOrder = async (exchange, symbol, market_type) => {
    if (!exchange || !symbol) return;
    try {
      const r = await axios.get(`${API}/market/min-order/${exchange}`,
        { params: { symbol, market_type: market_type || 'spot' }, withCredentials: true });
      setMinOrder(r.data);
    } catch { setMinOrder(null); }
  };

  const loadOpenTrades = async () => {
    try {
      const r = await axios.get(`${API}/trades`, { withCredentials: true });
      const all = r.data.trades || [];
      const open = all.filter(t =>
        !t.closed &&
        t.symbol === selectedSymbol &&
        t.exchange_id === selectedExchange
      );
      setOpenTrades(open);
    } catch {}
  };

  const closeTrade = async (trade) => {
    if (!window.confirm('¿Cerrar esta posición al precio actual?')) return;
    try {
      const r = await axios.post(`${API}/trades/${trade.trade_id}/close`, {}, { withCredentials: true });
      toast.success(`Cerrada: P&L $${r.data.pnl?.toFixed(2) ?? '0.00'}`);
      loadOpenTrades();
      loadPaperData();
      loadBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cerrar');
    }
  };

  const cancelPendingTrade = async (trade) => {
    if (!window.confirm('¿Cancelar esta orden límite pendiente?')) return;
    try {
      await axios.post(`${API}/trades/${trade.trade_id}/cancel`, {}, { withCredentials: true });
      toast.success('Orden cancelada');
      loadOpenTrades();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cancelar');
    }
  };

  const loadPaperData = async () => {
    try {
      const [pbRes, pfRes, payRes] = await Promise.all([
        axios.get(`${API}/paper/balance`, { withCredentials: true }).catch(() => null),
        // Sin límite de documentos (antes limit=500 -- misma clase de bug
        // de ventana deslizante que bajaba el P&L mostrado en Historial).
        axios.get(`${API}/portfolio/trades-full`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/payment/info`, { withCredentials: true }).catch(() => null),
      ]);
      if (pbRes) setPaperBal(pbRes.data);
      setInvestmentActive(payRes?.data?.investment_active ?? false);
      if (pfRes) {
        const trades = (pfRes.data.trades || [])
          .filter(t => (t.fuente === 'Trading Manual' || t.fuente === 'Copy Trading'));
        let pAcc = 0, rAcc = 0;
        setManualEquity(trades.map((t, i) => {
          if (t.paper_trading) pAcc = +(pAcc + t.pnl).toFixed(4);
          else rAcc = +(rAcc + t.pnl).toFixed(4);
          return {
            i,
            fecha: (t.ts || '').slice(5, 13).replace('T', ' '),
            paper: t.paper_trading ? pAcc : undefined,
            real: !t.paper_trading ? rAcc : undefined,
          };
        }));
      }
    } catch {}
  };

  useEffect(() => {
    loadExchanges();
    loadPaperData();
  }, []);

  useEffect(() => {
    if (selectedExchange) {
      loadMarkets();
      loadBalance();
    }
  }, [selectedExchange]);

  useEffect(() => {
    fetchMinOrder(selectedExchange, selectedSymbol, orderForm.market_type);
  }, [selectedExchange, selectedSymbol, orderForm.market_type]); // eslint-disable-line

  useEffect(() => {
    if (selectedExchange && selectedSymbol) {
      loadOpenTrades();
      const id = setInterval(loadOpenTrades, 8000);
      return () => clearInterval(id);
    }
  }, [selectedExchange, selectedSymbol]); // eslint-disable-line

  useEffect(() => {
    if (selectedExchange && selectedSymbol) {
      if (chartRef.current) {
        if (chartRef.current._handleResize) {
          window.removeEventListener('resize', chartRef.current._handleResize);
        }
        chartRef.current.chart.remove();
        chartRef.current = null;
      }
      loadMarketData(false);
      const interval = setInterval(() => loadMarketData(true), 60000);
      return () => clearInterval(interval);
    }
  }, [selectedExchange, selectedSymbol, selectedTimeframe]);

  useEffect(() => {
    if (markets.length > 0) {
      const filtered = markets.filter(m =>
        m.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredMarkets(filtered);
    }
  }, [searchQuery, markets]);

  const loadExchanges = async () => {
    try {
      const response = await axios.get(`${API}/exchanges/keys`, { withCredentials: true });
      const keys = response.data.keys || [];
      setExchanges(keys);
      if (keys.length > 0) {
        const fromUrl = _urlExchange && keys.find(k => k.exchange_id === _urlExchange);
        setSelectedExchange(fromUrl ? _urlExchange : keys[0].exchange_id);
      }
    } catch (error) {
      toast.error('Error al cargar exchanges');
    } finally {
      setLoading(false);
    }
  };

  const loadMarkets = async () => {
    setMarketsLoading(true);
    try {
      const response = await axios.get(
        `${API}/exchanges/${selectedExchange}/markets`,
        { withCredentials: true, timeout: 20000 }
      );
      const raw = response.data.markets || [];
      const symbols = raw.map(m => typeof m === 'string' ? m : (m.symbol || '')).filter(Boolean);
      const prioritized = [...TOP_MARKETS.filter(t => symbols.includes(t)), ...symbols.filter(s => !TOP_MARKETS.includes(s))];
      setMarkets(prioritized);
      setFilteredMarkets(searchQuery ? prioritized.filter(m => m.toLowerCase().includes(searchQuery.toLowerCase())) : prioritized);
    } catch (error) {
      console.error('Error loading markets:', error);
    } finally {
      setMarketsLoading(false);
    }
  };

  const loadBalance = async () => {
    setBalanceLoading(true);
    try {
      const response = await axios.get(
        `${API}/exchanges/${selectedExchange}/balance`,
        { withCredentials: true, timeout: 12000 }
      );
      setBalance(response.data);
    } catch (error) {
      console.error('Error loading balance:', error);
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const loadMarketData = async (silent = false) => {
    const sym = selectedSymbol;
    const exch = selectedExchange;
    activeSymbolRef.current = sym;
    activeExchangeRef.current = exch;
    if (!silent) setChartLoading(true);
    try {
      const chartResponse = await axios.get(
        `${API}/market/chart/${exch}`,
        { params: { symbol: sym, timeframe: selectedTimeframe }, withCredentials: true, timeout: 15000 }
      );
      if (activeSymbolRef.current !== sym || activeExchangeRef.current !== exch) return;
      if (chartResponse.data.data && chartResponse.data.data.length > 0) {
        updateChart(chartResponse.data.data, silent);
      }
    } catch (error) {
      console.error('Error loading market data:', error);
    } finally {
      if (activeSymbolRef.current === sym && activeExchangeRef.current === exch) {
        if (!silent) setChartLoading(false);
      }
    }
  };

  const updateChart = (data, silent = false) => {
    if (!chartContainerRef.current) return;

    if (!chartRef.current) {
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 600,
        layout: {
          background: { color: '#121212' },
          textColor: '#A1A1AA',
          attributionLogo: false,
        },
        grid: {
          vertLines: { color: '#1E1E1E' },
          horzLines: { color: '#1E1E1E' },
        },
        crosshair: {
          mode: 1,
        },
        rightPriceScale: {
          borderColor: '#27272A',
        },
        timeScale: {
          borderColor: '#27272A',
          timeVisible: true,
        },
      });

      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#00FF88',
        downColor: '#FF3B30',
        borderVisible: false,
        wickUpColor: '#00FF88',
        wickDownColor: '#FF3B30',
      });

      const ema20Series = chart.addSeries(LineSeries, { color: '#0a7bd4', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      const ema50Series = chart.addSeries(LineSeries, { color: '#f08c1a', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      const bbUpSeries = chart.addSeries(LineSeries, { color: 'rgba(120,140,170,0.55)', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      const bbLoSeries = chart.addSeries(LineSeries, { color: 'rgba(120,140,170,0.55)', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      const vwapSeries = chart.addSeries(LineSeries, { color: '#8e44ad', lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' }, priceScaleId: 'vol',
      });
      chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });

      chartRef.current = { chart, series: candlestickSeries, ema20Series, ema50Series, bbUpSeries, bbLoSeries, vwapSeries, volumeSeries };

      const handleResize = () => {
        if (chartContainerRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };
      window.addEventListener('resize', handleResize);
      chartRef.current._handleResize = handleResize;
    }

    const chartData = data.map(candle => ({
      time: Math.floor(candle[0] / 1000),
      open: candle[1],
      high: candle[2],
      low: candle[3],
      close: candle[4]
    }));

    chartRef.current.series.setData(chartData);

    // Heredar la vela en vivo de la última vela real que acaba de traer el
    // servidor (en vez de vaciarla a null) -- así el próximo tick de precio
    // sigue esa vela con su open/high/low correctos, y lastChartTimeRef
    // queda sincronizado con lo que el gráfico realmente tiene cargado
    // (ver el guard de tiempo en updateLivePrice más arriba).
    const _ultima = chartData[chartData.length - 1];
    if (_ultima) {
      liveCandleRef.current = { time: _ultima.time, open: _ultima.open, high: _ultima.high, low: _ultima.low, close: _ultima.close };
      lastChartTimeRef.current = _ultima.time;
    } else {
      liveCandleRef.current = null;
      lastChartTimeRef.current = 0;
    }

    lastDataRef.current = data;
    applyIndicators(data);
    if (!silent) chartRef.current.chart.timeScale().fitContent();

    // Flechas de operaciones (bots / manuales) — solo si están activadas
    if (chartRef.current?._markersPlugin) {
      try { chartRef.current._markersPlugin.detach(); } catch {}
      chartRef.current._markersPlugin = null;
    }
    if (indicators.botArrows || indicators.manualArrows) {
      const opts = { withCredentials: true };
      const reqs = [];
      if (indicators.botArrows) reqs.push(axios.get(`${API}/aegis/trades?limit=50`, opts).catch(() => ({ data: { trades: [] } })));
      else reqs.push(Promise.resolve({ data: { trades: [] } }));
      if (indicators.manualArrows) reqs.push(axios.get(`${API}/trades?limit=50`, opts).catch(() => ({ data: { trades: [] } })));
      else reqs.push(Promise.resolve({ data: { trades: [] } }));
      Promise.all(reqs).then(([botRes, manualRes]) => {
        const botTrades = (botRes.data.trades || []).map(t => ({
          action: t.action, symbol: t.symbol, price: t.price, ts: t.timestamp,
        }));
        const manualTrades = (manualRes.data.trades || []).map(t => ({
          action: t.side, symbol: t.symbol, price: t.price || t.exec_price, ts: t.created_at,
        }));
        const all = [...botTrades, ...manualTrades].filter(t => t.symbol === selectedSymbol && t.price && t.ts);
        if (all.length > 0 && chartRef.current?.series) {
          const tfSecs = {'1m':60,'5m':300,'15m':900,'30m':1800,'1h':3600,'2h':7200,'4h':14400,'1d':86400}[selectedTimeframe] || 3600;
          const chartTimes = (lastDataRef.current || []).map(c => Math.floor(c[0] / 1000));
          const first = chartTimes[0] || 0;
          const last = chartTimes[chartTimes.length - 1] || 0;
          const markers = all.map(t => {
            const raw = Math.floor(new Date(t.ts).getTime() / 1000);
            return {
              time: Math.floor(raw / tfSecs) * tfSecs,
              position: t.action === 'buy' ? 'belowBar' : 'aboveBar',
              color: t.action === 'buy' ? '#00FF88' : '#FF3B30',
              shape: t.action === 'buy' ? 'arrowUp' : 'arrowDown',
              text: `${t.action === 'buy' ? 'B' : 'S'} ${Number(t.price).toFixed(0)}`,
            };
          }).filter(m => m.time >= first && m.time <= last).sort((a, b) => a.time - b.time);
          if (markers.length > 0) {
            chartRef.current._markersPlugin = createSeriesMarkers(chartRef.current.series, markers);
          }
        }
      });
    }
  };

  // ===== Herramientas de trading: cálculo de indicadores en el cliente =====
  const emaCalc = (vals, span) => {
    const k = 2 / (span + 1); const out = []; let prev = vals[0];
    vals.forEach((v, i) => { prev = i === 0 ? v : v * k + prev * (1 - k); out.push(prev); });
    return out;
  };

  const applyIndicators = (data) => {
    if (!chartRef.current || !data || data.length < 30) return;
    const t = data.map(c => Math.floor(c[0] / 1000));
    const close = data.map(c => c[4]);
    const { ema20Series, ema50Series, bbUpSeries, bbLoSeries, vwapSeries, volumeSeries } = chartRef.current;
    const line = (vals, from = 0) => vals.map((v, i) => ({ time: t[i], value: v })).slice(from);

    ema20Series.setData(indicators.ema20 ? line(emaCalc(close, 20), 20) : []);
    ema50Series.setData(indicators.ema50 ? line(emaCalc(close, 50), 50) : []);

    if (indicators.bb) {
      const up = [], lo = [];
      for (let i = 19; i < close.length; i++) {
        const win = close.slice(i - 19, i + 1);
        const m = win.reduce((a, b) => a + b, 0) / 20;
        const sd = Math.sqrt(win.reduce((a, b) => a + (b - m) ** 2, 0) / 20);
        up.push({ time: t[i], value: m + 2 * sd }); lo.push({ time: t[i], value: m - 2 * sd });
      }
      bbUpSeries.setData(up); bbLoSeries.setData(lo);
    } else { bbUpSeries.setData([]); bbLoSeries.setData([]); }

    if (indicators.vwap) {
      let cumPV = 0, cumV = 0; const vw = [];
      data.forEach((c, i) => { const tp = (c[2] + c[3] + c[4]) / 3; cumPV += tp * c[5]; cumV += c[5]; vw.push({ time: t[i], value: cumPV / (cumV || 1) }); });
      vwapSeries.setData(vw);
    } else vwapSeries.setData([]);

    volumeSeries.setData(indicators.volume ? data.map((c, i) => ({
      time: t[i], value: c[5], color: c[4] >= c[1] ? 'rgba(14,170,107,0.45)' : 'rgba(216,73,63,0.45)'
    })) : []);

    // Lectura en vivo: RSI(14) y MACD(12,26,9)
    let g = 0, l = 0;
    for (let i = close.length - 14; i < close.length; i++) {
      const d = close[i] - close[i - 1]; if (d > 0) g += d; else l -= d;
    }
    const rsi = l === 0 ? 100 : 100 - 100 / (1 + (g / 14) / (l / 14));
    const macdArr = emaCalc(close, 12).map((v, i) => v - emaCalc(close, 26)[i]);
    const sig = emaCalc(macdArr, 9);
    // Presión compradora vs vendedora (demanda/oferta) de las últimas 20 velas:
    // posición del cierre dentro del rango de cada vela, ponderada por volumen
    let dem = 0, tot = 0;
    data.slice(-20).forEach(c => {
      const rng = c[2] - c[3] || 1;
      dem += c[5] * ((c[4] - c[3]) / rng);
      tot += c[5];
    });
    const demanda = Math.round((dem / tot) * 100);
    // Estocástico %K(14)
    const h14 = Math.max(...data.slice(-14).map(c => c[2]));
    const l14 = Math.min(...data.slice(-14).map(c => c[3]));
    const stoch = Math.round(((close.at(-1) - l14) / ((h14 - l14) || 1)) * 100);
    setIndReadout({ rsi: rsi.toFixed(1), macd: (macdArr.at(-1) - sig.at(-1)).toFixed(2), demanda, stoch });
  };

  useEffect(() => {
    if (lastDataRef.current) {
      applyIndicators(lastDataRef.current);
    }
  }, [indicators]); // eslint-disable-line

  const handleTrade = async (e) => {
    e.preventDefault();
    if (!selectedExchange) {
      toast.error('Conecta un exchange primero');
      return;
    }

    try {
      await axios.post(`${API}/trades`, {
        exchange_id: selectedExchange,
        symbol: selectedSymbol,
        order_type: orderForm.type,
        side: orderForm.side,
        market_type: orderForm.market_type,
        amount: parseFloat(orderForm.amount),
        price: orderForm.price ? parseFloat(orderForm.price) : null,
        leverage: orderForm.leverage,
        stop_loss: orderForm.stop_loss ? parseFloat(orderForm.stop_loss) : null,
        take_profit: orderForm.take_profit ? parseFloat(orderForm.take_profit) : null,
        paper_trading: orderForm.paper_trading
      }, { withCredentials: true });
      
      toast.success('Orden ejecutada exitosamente');
      setOrderForm({ ...orderForm, amount: '', price: '', stop_loss: '', take_profit: '' });
      loadBalance();
      loadOpenTrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar orden');
    }
  };

  const getBaseQuote = () => {
    if (!selectedSymbol) return { base: '', quote: '' };
    const [base, quote] = selectedSymbol.split('/');
    return { base, quote };
  };

  const { base, quote } = getBaseQuote();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div>
        </div>
      </Layout>
    );
  }

  if (exchanges.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-white text-xl mb-4">No hay exchanges conectados</p>
            <p className="text-[#A1A1AA] mb-6">Conecta un exchange para comenzar a tradear</p>
            <Button onClick={() => window.location.href = '/settings'} className="bg-[#00D4FF] text-black">
              Ir a Configuración
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Select value={selectedExchange} onValueChange={setSelectedExchange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {exchanges.map(ex => (
                  <SelectItem key={ex.key_id} value={ex.exchange_id}>
                    {ex.exchange_id.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="text-white">
              <p className="text-xs text-[#A1A1AA]">Balance Real</p>
              <p className="text-lg font-bold mono">
                {balanceLoading ? <span className="text-[#52525B]">...</span> : `$${balance?.total_balance_usd?.toFixed(2) ?? '0.00'}`}
              </p>
            </div>
            <div className="w-px h-8 bg-[#27272A]" />
            <div className="text-white">
              <p className="text-xs text-[#A1A1AA]">Saldo Paper</p>
              <p className="text-lg font-bold text-[#00D4FF] mono">${paperBal?.saldo_actual?.toFixed(2) ?? '10,000.00'}</p>
            </div>
            {paperBal?.pnl_acumulado !== undefined && (
              <div className="text-white">
                <p className="text-xs text-[#A1A1AA]">P&L Manual</p>
                <p className={`text-sm font-bold mono ${paperBal.pnl_acumulado >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {paperBal.pnl_acumulado >= 0 ? '+' : ''}${paperBal.pnl_acumulado.toFixed(2)}
                </p>
              </div>
            )}
          </div>

          <Button onClick={async (e) => { const btn = e.currentTarget; btn.disabled = true; btn.querySelector('svg')?.classList.add('animate-spin'); await Promise.all([loadMarketData(), loadOpenTrades()]); btn.disabled = false; btn.querySelector('svg')?.classList.remove('animate-spin'); }} variant="outline" size="sm" className="gap-2 active:scale-95 transition-transform">
            <RefreshCw size={16} /> Actualizar
          </Button>
        </div>

        {/* Equity curva trading manual (aparece cuando hay datos) */}
        {manualEquity.length > 1 && (
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3">
            <div className="flex items-center gap-4 mb-1 text-xs text-[#A1A1AA]">
              <span className="font-medium text-white">Equity Manual</span>
              {manualEquity.some(d => d.paper !== undefined) && <span className="text-[#00D4FF]">● Paper</span>}
              {manualEquity.some(d => d.real !== undefined) && <span className="text-[#FFD600]">● Real</span>}
            </div>
            <ResponsiveContainer width="100%" height={80}>
              <LineChart data={manualEquity}>
                <XAxis dataKey="fecha" hide />
                <YAxis hide />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', fontSize: 11 }}
                  formatter={(v, n) => [`$${Number(v).toFixed(4)}`, n === 'paper' ? 'Paper' : 'Real']}
                />
                {manualEquity.some(d => d.paper !== undefined) && (
                  <Line type="monotone" dataKey="paper" stroke="#00D4FF" strokeWidth={2} dot={false} connectNulls />
                )}
                {manualEquity.some(d => d.real !== undefined) && (
                  <Line type="monotone" dataKey="real" stroke="#FFD600" strokeWidth={2} dot={false} connectNulls />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-2 bg-[#121212] border border-[#27272A] rounded-sm p-4 h-[calc(100vh-180px)] overflow-y-auto">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A1A1AA]" size={16} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar par..."
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-1">
              {filteredMarkets.map((market) => (
                <button
                  key={market}
                  onClick={() => setSelectedSymbol(market)}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                    selectedSymbol === market
                      ? 'bg-[#00D4FF]/10 text-[#00D4FF] border-l-2 border-[#00D4FF]'
                      : 'text-[#A1A1AA] hover:bg-[#1E1E1E]'
                  }`}
                >
                  {market}
                </button>
              ))}
              {marketsLoading && (
                <p className="text-[10px] text-[#52525B] text-center py-2">Cargando todos los pares...</p>
              )}
            </div>
          </div>

          <div className="col-span-7 bg-[#121212] border border-[#27272A] rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-baseline gap-3">
                  <h2 className="text-2xl font-bold text-white mono">{selectedSymbol}</h2>
                  {livePrice?.last && <span className={`text-xl font-bold mono ${liveCandleRef.current && livePrice.last >= liveCandleRef.current.open ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>${livePrice.last < 1 ? livePrice.last.toFixed(8) : livePrice.last.toFixed(2)}</span>}
                </div>
                <p className="text-xs text-[#A1A1AA]">{selectedExchange.toUpperCase()} · {livePrice?.last ? 'EN VIVO' : 'Cargando...'}</p>
              </div>
              <div className="flex items-center gap-1 bg-[#1E1E1E] rounded-lg p-1">
                {['1m','5m','15m','30m','1h','2h','4h','1d'].map(tf => (
                  <button key={tf} onClick={() => setSelectedTimeframe(tf)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${selectedTimeframe === tf ? 'bg-[#00D4FF] text-black shadow-sm' : 'text-[#A1A1AA] hover:text-white hover:bg-[#27272A]'}`}>
                    {tf}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-2 text-xs">
              {[['ema20','EMA 20'],['ema50','EMA 50'],['bb','Bollinger'],['vwap','VWAP'],['volume','Vol'],['botArrows','Bots ↕'],['manualArrows','Manual ↕']].map(([k, lbl]) => (
                <button key={k} onClick={() => setIndicators(p => ({ ...p, [k]: !p[k] }))}
                  className={`px-2.5 py-1 rounded-md font-medium transition-all ${indicators[k] ? 'bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/30' : 'bg-[#1E1E1E] text-[#52525B] border border-transparent hover:text-[#A1A1AA]'}`}>
                  {lbl}
                </button>
              ))}
              {indReadout && (
                <span className="ml-auto text-[#A1A1AA]">
                  RSI(14): <span className={indReadout.rsi > 70 ? 'text-red-400' : indReadout.rsi < 30 ? 'text-green-400' : 'text-white'}>{indReadout.rsi}</span>
                  {' '}· MACD: <span className={indReadout.macd >= 0 ? 'text-green-400' : 'text-red-400'}>{indReadout.macd}</span>
                  {' '}· Stoch %K: <span className="text-white">{indReadout.stoch}</span>
                </span>
              )}
            </div>
            {indReadout && (
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="text-[#A1A1AA]">Oferta</span>
                <div className="flex-1 h-2.5 rounded overflow-hidden flex" title={`Presión compradora: ${indReadout.demanda}%`}>
                  <div style={{ width: `${indReadout.demanda}%`, background: '#0eaa6b' }} />
                  <div style={{ width: `${100 - indReadout.demanda}%`, background: '#d8493f' }} />
                </div>
                <span className="text-[#A1A1AA]">Demanda</span>
                <span className={indReadout.demanda >= 55 ? 'text-green-400 font-bold' : indReadout.demanda <= 45 ? 'text-red-400 font-bold' : 'text-white'}>
                  {indReadout.demanda >= 55 ? `MAYOR DEMANDA ${indReadout.demanda}%` : indReadout.demanda <= 45 ? `MAYOR OFERTA ${100 - indReadout.demanda}%` : `EQUILIBRIO ${indReadout.demanda}%`}
                </span>
              </div>
            )}
            <div style={{ position: 'relative', display: 'flex' }}>
              <DrawingTools chartRef={chartRef} width={860} height={400} />
              <div style={{ position: 'relative', flex: 1 }}>
                <div ref={chartContainerRef} style={{ flex: 1 }} />
                {chartLoading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(18,18,18,0.7)', zIndex: 10 }}>
                    <div className="text-center">
                      <div className="aegis-spinner mx-auto mb-2" />
                      <p className="text-[#A1A1AA] text-xs">Cargando gráfico...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-3 bg-[#121212] border border-[#27272A] rounded-sm p-4">
            {/* Indicador de modo trading — determinado por estado de inversión */}
            <div className={`flex items-center gap-2 mb-3 px-3 py-1.5 rounded-lg text-xs font-bold ${investmentActive ? 'bg-[#FFD600]/10 text-[#FFD600] border border-[#FFD600]/30' : 'bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/30'}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              {investmentActive ? '⚡ MODO REAL — Operando con inversión activa' : '📋 MODO PAPER — Sin inversión activa (simulado)'}
            </div>
            <Tabs defaultValue="spot">
              <TabsList className="grid grid-cols-2 w-full mb-4 bg-[#1E1E1E]">
                <TabsTrigger 
                  value="spot" 
                  onClick={() => setOrderForm({...orderForm, market_type: 'spot'})}
                >
                  Spot
                </TabsTrigger>
                <TabsTrigger 
                  value="futures"
                  onClick={() => setOrderForm({...orderForm, market_type: 'futures'})}
                >
                  Futures
                </TabsTrigger>
              </TabsList>

              <TabsContent value="spot">
                <form onSubmit={handleTrade} className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setOrderForm({...orderForm, side: 'buy'})}
                      className={`py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all ${orderForm.side === 'buy' ? 'bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black shadow-[0_0_12px_rgba(0,255,136,0.3)]' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:bg-[#27272A]'}`}>
                      COMPRAR
                    </button>
                    <button type="button" onClick={() => setOrderForm({...orderForm, side: 'sell'})}
                      className={`py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all ${orderForm.side === 'sell' ? 'bg-gradient-to-r from-[#FF3B30] to-[#FF6B60] text-white shadow-[0_0_12px_rgba(255,59,48,0.3)]' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:bg-[#27272A]'}`}>
                      VENDER
                    </button>
                  </div>

                  <div>
                    <Label>Tipo de Orden</Label>
                    <Select value={orderForm.type} onValueChange={(v) => setOrderForm({...orderForm, type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="market">Market</SelectItem>
                        <SelectItem value="limit">Limit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {orderForm.type === 'limit' && (
                    <div>
                      <Label>Precio ({quote})</Label>
                      <Input
                        type="number"
                        step="any"
                        value={orderForm.price}
                        onChange={(e) => setOrderForm({...orderForm, price: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Cantidad ({base})</Label>
                    <Input
                      type="number"
                      step="any"
                      value={orderForm.amount}
                      onChange={(e) => setOrderForm({...orderForm, amount: e.target.value})}
                      required
                      placeholder="0.00"
                    />
                    {minOrder?.min_amount != null && (
                      <p className="text-[10px] text-[#A1A1AA] mt-1">
                        Mínimo: <span className="text-[#00D4FF] font-mono">{minOrder.min_amount} {base}</span>
                        {minOrder.min_cost_usdt != null && <span> · ~${minOrder.min_cost_usdt} USDT</span>}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Stop Loss</Label>
                      <Input
                        type="number"
                        step="any"
                        value={orderForm.stop_loss}
                        onChange={(e) => setOrderForm({...orderForm, stop_loss: e.target.value})}
                        placeholder="Opcional"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Take Profit</Label>
                      <Input
                        type="number"
                        step="any"
                        value={orderForm.take_profit}
                        onChange={(e) => setOrderForm({...orderForm, take_profit: e.target.value})}
                        placeholder="Opcional"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  {orderForm.amount && (orderForm.type === 'market' || orderForm.price) && (
                    <div className="bg-[#1E1E1E] p-3 rounded-sm">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A1A1AA]">Total</span>
                        <span className="text-white mono font-bold">
                          {orderForm.type === 'limit'
                            ? (parseFloat(orderForm.amount) * parseFloat(orderForm.price || 0)).toFixed(2) + ` ${quote}`
                            : livePrice?.last
                              ? `≈${(parseFloat(orderForm.amount) * livePrice.last).toFixed(2)} ${quote}`
                              : `~Market ${quote}`
                          }
                        </span>
                      </div>
                    </div>
                  )}

                  <button type="submit"
                    className={`w-full py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide transition-all ${
                      orderForm.side === 'buy'
                        ? 'bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black shadow-[0_0_12px_rgba(0,255,136,0.3)]'
                        : 'bg-gradient-to-r from-[#FF3B30] to-[#FF6B60] text-white shadow-[0_0_12px_rgba(255,59,48,0.3)]'
                    }`}>
                    {orderForm.side === 'buy' ? 'COMPRAR' : 'VENDER'} {base}
                  </button>
                </form>
              </TabsContent>

              <TabsContent value="futures">
                <form onSubmit={handleTrade} className="space-y-4">
                  <div>
                    <Label className="text-xs text-[#A1A1AA]">Apalancamiento: <span className="text-white font-bold">{orderForm.leverage}x</span></Label>
                    <Input
                      type="range"
                      min="1"
                      max="125"
                      value={orderForm.leverage}
                      onChange={(e) => setOrderForm({...orderForm, leverage: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-[#52525B] mt-1">
                      <span>1x</span>
                      <span>125x</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setOrderForm({...orderForm, side: 'buy'})}
                      className={`py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all ${orderForm.side === 'buy' ? 'bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black shadow-[0_0_12px_rgba(0,255,136,0.3)]' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:bg-[#27272A]'}`}>
                      LONG
                    </button>
                    <button type="button" onClick={() => setOrderForm({...orderForm, side: 'sell'})}
                      className={`py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all ${orderForm.side === 'sell' ? 'bg-gradient-to-r from-[#FF3B30] to-[#FF6B60] text-white shadow-[0_0_12px_rgba(255,59,48,0.3)]' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:bg-[#27272A]'}`}>
                      SHORT
                    </button>
                  </div>

                  <div>
                    <Label>Cantidad ({base})</Label>
                    <Input
                      type="number"
                      step="any"
                      value={orderForm.amount}
                      onChange={(e) => setOrderForm({...orderForm, amount: e.target.value})}
                      required
                      placeholder="0.00"
                    />
                    {minOrder?.min_amount != null && (
                      <p className="text-[10px] text-[#A1A1AA] mt-1">
                        Mínimo: <span className="text-[#00D4FF] font-mono">{minOrder.min_amount} {base}</span>
                        {minOrder.min_cost_usdt != null && <span> · ~${minOrder.min_cost_usdt} USDT</span>}
                      </p>
                    )}
                    {orderForm.amount && livePrice?.last && (
                      <div className="bg-[#1E1E1E] p-3 rounded-sm mt-2 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Valor de la posición</span>
                          <span className="text-white mono font-bold">
                            ≈${(parseFloat(orderForm.amount) * livePrice.last).toFixed(2)} USDT
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-[#A1A1AA]">Margen requerido ({orderForm.leverage}x)</span>
                          <span className="text-[#00D4FF] mono">
                            ≈${(parseFloat(orderForm.amount) * livePrice.last / Math.max(orderForm.leverage, 1)).toFixed(2)} USDT
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Stop Loss</Label>
                      <Input
                        type="number"
                        step="any"
                        value={orderForm.stop_loss}
                        onChange={(e) => setOrderForm({...orderForm, stop_loss: e.target.value})}
                        placeholder="Opcional"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Take Profit</Label>
                      <Input
                        type="number"
                        step="any"
                        value={orderForm.take_profit}
                        onChange={(e) => setOrderForm({...orderForm, take_profit: e.target.value})}
                        placeholder="Opcional"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <button type="submit"
                    className={`w-full py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide transition-all ${
                      orderForm.side === 'buy'
                        ? 'bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black shadow-[0_0_12px_rgba(0,255,136,0.3)]'
                        : 'bg-gradient-to-r from-[#FF3B30] to-[#FF6B60] text-white shadow-[0_0_12px_rgba(255,59,48,0.3)]'
                    }`}>
                    {orderForm.side === 'buy' ? 'LONG' : 'SHORT'} {orderForm.leverage}x
                  </button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Panel de posiciones abiertas con P&L en vivo */}
            {openTrades.length > 0 && (
              <div className="mt-4 border-t border-[#27272A] pt-4">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2">Posiciones Abiertas</p>
                <div className="space-y-2">
                  {openTrades.map((t) => {
                    const isPending = t.status === 'pending';
                    const entry = parseFloat(t.price) || 0;
                    const current = livePrice?.last;
                    const amount = parseFloat(t.amount) || 0;
                    const isBuy = t.side === 'buy';
                    const hasEntry = entry > 0;
                    const feeRate = t.market_type === 'futures' ? 0.00055 : 0.001;
                    const feesTotal = !isPending && hasEntry && current ? (entry + current) * amount * feeRate : 0;
                    const rawPnl = !isPending && hasEntry && current ? (isBuy ? (current - entry) : (entry - current)) * amount : null;
                    const netPnl = rawPnl !== null ? rawPnl - feesTotal : null;
                    const netPct = hasEntry && netPnl !== null ? (netPnl / (entry * amount)) * 100 : null;
                    const isProfit = netPnl !== null && netPnl >= 0;
                    const rentable = netPnl !== null && netPnl > 0;

                    return (
                      <div key={t.trade_id} className="bg-[#1A1A1A] border border-[#333333] rounded-lg p-3 text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-0.5 rounded font-bold text-[10px] ${isBuy ? 'bg-[#00FF88]/15 text-[#00FF88]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'}`}>
                            {isBuy ? 'COMPRA' : 'VENTA'}{t.leverage > 1 ? ` ${t.leverage}x` : ''}
                          </span>
                          <span className="text-[#A1A1AA] font-medium">{t.symbol}</span>
                          {isPending ? (
                            <span className="px-2 py-0.5 rounded font-bold text-[10px] bg-[#FF6B35]/15 text-[#FF6B35]">
                              PENDIENTE
                            </span>
                          ) : netPnl !== null ? (
                            <span className={`font-bold mono text-sm ${isProfit ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                              {isProfit ? '+' : ''}{netPnl.toFixed(4)} USDT
                            </span>
                          ) : (
                            <span className="text-[#71717A]">—</span>
                          )}
                        </div>
                        {isPending && (
                          <p className="text-[10px] text-[#FF6B35] mb-2">
                            Orden límite esperando que el precio llegue a ${entry < 1 ? entry.toFixed(8) : entry.toFixed(2)}
                          </p>
                        )}
                        <div className="grid grid-cols-3 gap-1 text-[10px]">
                          <div>
                            <p className="text-[#71717A] mb-0.5">{isPending ? 'Precio límite' : 'Entrada'}</p>
                            <p className="text-[#E4E4E7] mono font-bold text-xs">
                              {hasEntry ? `$${entry < 1 ? entry.toFixed(8) : entry.toFixed(2)}` : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#71717A] mb-0.5">Actual</p>
                            <p className="text-[#00D4FF] mono font-bold text-xs">
                              {current ? `$${current < 1 ? current.toFixed(8) : current.toFixed(2)}` : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#71717A] mb-0.5">P&L neto</p>
                            <p className={`mono font-bold text-sm ${isProfit ? 'text-[#00FF88]' : netPnl !== null ? 'text-[#FF3B30]' : 'text-[#71717A]'}`}>
                              {isPending ? '—' : netPct !== null ? `${isProfit ? '+' : ''}${netPct.toFixed(3)}%` : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#2A2A2A]">
                          <span className="text-[#71717A]">Cant: <span className="text-[#E4E4E7] font-medium">{amount}</span></span>
                          <span className="text-[#71717A]">Fees: <span className="text-[#E4E4E7] font-medium">{feesTotal > 0 ? `$${feesTotal.toFixed(4)}` : '—'}</span></span>
                          <span className={`text-[10px] font-bold ${isPending ? 'text-[#FF6B35]' : !hasEntry || !current ? 'text-[#71717A]' : rentable ? 'text-[#00FF88]' : 'text-[#FF6B35]'}`}>
                            {isPending ? 'ESPERANDO PRECIO' : !current ? 'SIN PRECIO' : !hasEntry ? 'SIN ENTRADA' : rentable ? '✓ RENTABLE' : '✗ EN PÉRDIDA'}
                          </span>
                        </div>
                        {(t.stop_loss || t.take_profit) && (
                          <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                            {t.stop_loss && (
                              <span className="text-[#FF3B30]">SL: <span className="font-mono font-bold">${t.stop_loss}</span></span>
                            )}
                            {t.take_profit && (
                              <span className="text-[#00FF88]">TP: <span className="font-mono font-bold">${t.take_profit}</span></span>
                            )}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => isPending ? cancelPendingTrade(t) : closeTrade(t)}
                          className="w-full mt-2 py-1.5 rounded-md text-[11px] font-bold bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors"
                        >
                          {isPending ? 'Cancelar orden' : `Cerrar posición${t.paper_trading ? ' (paper)' : ''}`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TradingPro;